import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, abs, errMsg, TYPE_LABELS } from "../../lib/api";
import { Loading } from "../../components/PageHeader";
import { AdminTitle, ErrorBox } from "./AdminLayout";

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
const FIELDS = ["price", "stock", "shipping_my", "shipping_intl"];
const HEADERS = [["thumb", ""], ["product", "Product"], ["price", "Price (USD)"], ["stock", "Stock"], ["ship-my", "Ship MY"], ["ship-intl", "Ship Intl"]];
const CELL = "wb-input !py-1.5 !px-2 text-sm font-mono";
const invalid = (r) => !(Number(r.price) > 0) || (r.type === "physical_art" && (Number(r.stock) < 0 || !Number.isInteger(Number(r.stock))));
const toItem = (r) => ({ id: r.id, price: Number(r.price), ...(r.type === "physical_art" ? { stock: Number(r.stock), shipping_my: Number(r.shipping_my), shipping_intl: Number(r.shipping_intl) } : {}) });

function usePrices() {
  const [rows, setRows] = useState(null);
  const [orig, setOrig] = useState({});
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get("/admin/products").then((r) => {
    const m = Object.fromEntries(r.data.map((p) => [p.id, { price: p.price, stock: p.stock, shipping_my: p.shipping_my, shipping_intl: p.shipping_intl }]));
    setOrig(m); setRows(r.data.map((p) => ({ ...p, ...m[p.id] })));
  }).catch((e) => setErr(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const set = (id, k, v) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [k]: v } : r)));
  const dirty = useMemo(() => (rows || []).filter((r) => FIELDS.some((k) => num(r[k]) !== num(orig[r.id]?.[k]))), [rows, orig]);
  return { rows, err, dirty, set, load };
}

const ShipCell = ({ r, field, editable, fallback, set }) => (
  <td className="px-3 py-2">{editable
    ? <input type="number" min="0" step="0.01" className={`${CELL} !w-24`} value={r[field]} onChange={(e) => set(r.id, field, e.target.value)} data-testid={`${field === "shipping_my" ? "ship-my" : "ship-intl"}-input-${r.slug}`} />
    : <span className="text-xs muted">{fallback}</span>}</td>
);

function PriceRow({ r, isDirty, set }) {
  const phys = r.type === "physical_art";
  return (
    <tr className={`border-b wb-line ${invalid(r) ? "bg-red-500/10" : isDirty ? "bg-wb-card" : ""}`} data-testid={`price-row-${r.slug}`}>
      <td className="px-3 py-2">{r.image_urls[0] && <img src={abs(r.image_urls[0])} alt="" className="h-10 w-10 object-cover" />}</td>
      <td className="px-3 py-2"><div>{r.title}</div><div className="text-xs muted">{TYPE_LABELS[r.type]}{!r.active && " · draft"}</div></td>
      <td className="px-3 py-2"><input type="number" step="0.01" min="0.01" className={`${CELL} !w-28`} value={r.price} onChange={(e) => set(r.id, "price", e.target.value)} data-testid={`price-input-${r.slug}`} /></td>
      <td className="px-3 py-2">{phys ? <input type="number" min="0" step="1" className={`${CELL} !w-20`} value={r.stock ?? 0} onChange={(e) => set(r.id, "stock", e.target.value)} data-testid={`stock-input-${r.slug}`} /> : <span className="muted">—</span>}</td>
      <ShipCell r={r} field="shipping_my" set={set} editable={phys && r.shipping_mode !== "free"} fallback={phys ? "Free" : "—"} />
      <ShipCell r={r} field="shipping_intl" set={set} editable={phys && r.shipping_mode === "flat"} fallback={phys ? (r.shipping_mode === "free" ? "Free" : "MY only") : "—"} />
    </tr>
  );
}

export default function AdminPrices() {
  const { rows, err, dirty, set, load } = usePrices();
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (dirty.some(invalid)) return toast.error("Prices must be greater than 0 and stock a whole number ≥ 0");
    setBusy(true);
    try {
      const { data } = await api.patch("/admin/prices", { items: dirty.map(toItem) });
      toast.success(`${data.changed} product(s) updated`); load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  if (err) return <ErrorBox msg={err} />;
  if (!rows) return <Loading />;
  return (
    <div data-testid="admin-prices">
      <AdminTitle eyebrow="Catalogue" title="Prices & stock">
        <button onClick={save} disabled={busy || !dirty.length} className="btn-gold" data-testid="prices-save-button">{busy ? "Saving…" : `Save ${dirty.length || ""} change${dirty.length === 1 ? "" : "s"}`}</button>
      </AdminTitle>
      <p className="mb-6 text-xs muted">Changes apply to new checkouts only — orders already paid keep their original prices.</p>
      <div className="overflow-x-auto border wb-line">
        <table className="w-full text-sm">
          <thead><tr className="border-b wb-line text-left">{HEADERS.map(([k, h]) => <th key={k} className="px-3 py-3 font-mono text-[0.62rem] uppercase tracking-[0.2em] font-normal muted">{h}</th>)}</tr></thead>
          <tbody>{rows.map((r) => <PriceRow key={r.id} r={r} isDirty={dirty.includes(r)} set={set} />)}</tbody>
        </table>
      </div>
    </div>
  );
}
